import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type {
  LoyaltyBalanceResult,
  LoyaltyOrgSummary,
  LoyaltyProgram,
  LoyaltyProgramInput,
} from '../domain/loyaltyTypes';

function mapProgram(raw: Record<string, unknown> | null | undefined): LoyaltyProgram | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = typeof raw.id === 'string' ? raw.id : null;
  if (!id) return null;
  // #769: NaN-safe parsing con fallback explícito
  const pointsPerArs = Number(raw.pointsPerArs ?? raw.points_per_ars ?? 100);
  const redeemArsPerPoint = Number(raw.redeemArsPerPoint ?? raw.redeem_ars_per_point ?? 1);
  const minRedeemPoints = Number(raw.minRedeemPoints ?? raw.min_redeem_points ?? 1);
  return {
    id,
    pointsPerArs: Number.isFinite(pointsPerArs) ? pointsPerArs : 100,
    redeemArsPerPoint: Number.isFinite(redeemArsPerPoint) ? redeemArsPerPoint : 1,
    minRedeemPoints: Number.isFinite(minRedeemPoints) ? minRedeemPoints : 1,
    isActive: raw.isActive === true || raw.is_active === true,
  };
}

export async function fetchLoyaltyBalance(customerId: string): Promise<LoyaltyBalanceResult> {
  const { data, error } = await supabase.schema('customers').rpc('loyalty_get_balance', {
    p_customer_id: customerId,
  });
  if (error) throw new Error(error.message);
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    ok: row.ok !== false,
    balance: Number(row.balance ?? 0),
    program: mapProgram(row.program as Record<string, unknown> | null),
    code: typeof row.code === 'string' ? row.code : undefined,
  };
}

export async function fetchLoyaltyOrgSummary(): Promise<LoyaltyOrgSummary> {
  const { data, error } = await supabase.schema('customers').rpc('loyalty_get_org_summary');
  if (error) throw new Error(error.message);
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    ok: row.ok !== false,
    earned: Number(row.earned ?? 0),
    redeemed: Number(row.redeemed ?? 0),
    liabilityPoints: Number(row.liabilityPoints ?? 0),
    accounts: Number(row.accounts ?? 0),
  };
}

export async function applyLoyaltyProgramRemote(
  operation: 'insert' | 'update' | 'delete',
  input: LoyaltyProgramInput,
): Promise<{ ok: boolean; id?: string }> {
  const payload = {
    id: input.id ?? undefined,
    pointsPerArs: input.pointsPerArs,
    redeemArsPerPoint: input.redeemArsPerPoint,
    minRedeemPoints: input.minRedeemPoints,
    isActive: input.isActive,
    branchId: input.branchId,
    operationId: input.operationId,
    updatedAt: new Date().toISOString(),
  };
  const { data, error } = await supabase.schema('customers').rpc('apply_loyalty_program_command', {
    p_operation: operation,
    p_payload: payload,
  });
  if (error) {
    captureOpsFailure({
      module: 'loyalty',
      op: 'loyalty.program_write',
      error,
      ingestError: error.message,
      itemId: input.id ?? input.operationId,
      extra: { programId: input.id ?? undefined, operation },
    });
    throw new Error(error.message);
  }
  const row = (data ?? {}) as Record<string, unknown>;
  if (row.ok === false) {
    const fail = new Error(typeof row.code === 'string' ? row.code : 'loyalty_save_failed');
    captureOpsFailure({
      module: 'loyalty',
      op: 'loyalty.program_write',
      error: fail,
      ingestError: fail.message,
      applied: false,
      itemId: input.id ?? input.operationId,
      extra: { programId: input.id ?? undefined, operation },
    });
    throw fail;
  }
  return { ok: true, id: typeof row.id === 'string' ? row.id : undefined };
}

export async function applyLoyaltySaleEffects(args: {
  customerId: string;
  saleId: string;
  paidAmount: number;
  redeemPoints: number;
}): Promise<void> {
  // #743: enviar snake_case keys + chequear data.ok
  const { data, error } = await supabase.schema('customers').rpc('loyalty_apply_sale_effects', {
    p_payload: {
      customer_id: args.customerId,
      sale_id: args.saleId,
      paid_amount: args.paidAmount,
      redeem_points: args.redeemPoints,
    },
  });
  if (error) {
    captureOpsFailure({
      module: 'loyalty',
      op: 'loyalty.sale_effects',
      error,
      ingestError: error.message,
      itemId: args.saleId,
      extra: { saleId: args.saleId, customerId: args.customerId },
    });
    console.warn('[loyalty] apply_sale_effects', error.message);
    return;
  }
  const result = (data ?? {}) as { ok?: boolean; code?: string };
  if (result.ok === false) {
    captureOpsFailure({
      module: 'loyalty',
      op: 'loyalty.sale_effects',
      error: new Error(result.code ?? 'loyalty_apply_failed'),
      ingestError: result.code ?? 'loyalty_apply_failed',
      applied: false,
      itemId: args.saleId,
      extra: { saleId: args.saleId, customerId: args.customerId },
    });
    console.warn('[loyalty] apply_sale_effects rejected:', result.code);
  }
}

export async function fetchActiveLoyaltyProgram(): Promise<LoyaltyProgram | null> {
  const { data, error } = await supabase
    .schema('customers')
    .from('loyalty_programs')
    .select('id, points_per_ars, redeem_ars_per_point, min_redeem_points, is_active')
    .is('deleted_at', null)
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  if (error) {
    console.warn('[loyalty] fetch program', error.message);
    return null;
  }
  return mapProgram(data as Record<string, unknown> | null);
}
